import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { AUTH_MODE } from './auth/mode';
import { loadConfig } from './auth/config';
import { setToken } from './auth/tokenAccess';
import { getUserManager } from './auth/userManager';
import { MOCK_ADMIN_TOKEN } from './mocks/mockAuth';
import { applyTheme, resolveInitialTheme } from './shared/theme/theme';
import './styles/tailwind.css';
import './styles/global.scss';

// Apply the persisted / preferred theme before first paint to avoid a flash.
applyTheme(resolveInitialTheme());

async function enableMocking() {
  if (import.meta.env.VITE_MSW !== 'true') return;
  const { worker } = await import('./mocks/browser');
  return worker.start({
    onUnhandledRequest: 'warn',
    serviceWorker: {
      url: import.meta.env.BASE_URL + 'mockServiceWorker.js',
    },
  });
}

function render() {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

/**
 * Seed the access token from any still-valid stored session BEFORE the first
 * render, so the identity read by `useIdentity` (decoded from the token's claims)
 * and the initial authed API requests both see a bearer.
 *
 * oidc-client-ts does NOT emit `userLoaded` when it merely restores a still-valid
 * user from storage (only on a fresh signin/renew), and syncing the token from a
 * React effect runs too late — child effects fire before the provider's, so the
 * first authed requests would go out bearer-less on a hard refresh and bounce
 * through a 401 + silent renew. Doing it here, outside React, also avoids an impure render-phase side
 * effect. In-session signin/renew/logout stay covered by the userManager events,
 * and a mid-session 401 is repaired by the baseQuery silent-renew path.
 */
async function primeAccessToken() {
  try {
    const user = await getUserManager().getUser();
    if (user && !user.expired && user.access_token) {
      setToken(user.access_token);
    }
  } catch (err) {
    // No stored session (or storage/config unavailable) — stay anonymous; the
    // route guards will redirect to the IdP login.
    console.warn('Access-token prime skipped:', err);
  }
}

/**
 * Load the runtime config without making it fatal. The oidc path awaits
 * loadConfig() directly because the UserManager cannot be built without it;
 * dev and msw modes only need it for optional extras (the Configuration
 * page's tool links), so a missing config.json must not block startup.
 */
async function loadOptionalConfig() {
  try {
    await loadConfig();
  } catch (err) {
    console.debug('Runtime config not loaded (optional in this mode):', err);
  }
}

async function bootstrap() {
  if (AUTH_MODE === 'msw') {
    // Mock mode is a regular (non-admin) user by default. Opt in to an admin
    // identity with VITE_MSW_ADMIN=true so the admin-only Configuration page is
    // reachable against MSW.
    if (import.meta.env.VITE_MSW_ADMIN === 'true') {
      setToken(MOCK_ADMIN_TOKEN);
    }
    await loadOptionalConfig();
    await enableMocking();
    render();
    return;
  }
  if (AUTH_MODE === 'dev') {
    const { initDevAuth } = await import('./auth/devAuth');
    initDevAuth();
    await loadOptionalConfig();
    render();
    return;
  }
  // oidc (production default): config required before the UserManager is built.
  await loadConfig();
  await primeAccessToken();
  render();
}

try {
  await bootstrap();
} catch (err) {
  console.error('App bootstrap failed:', err);
  const root = document.getElementById('root');
  if (root) {
    root.textContent = 'Failed to start: could not load application configuration.';
  }
}
